<template>
    <div class="pagination-container" v-if="totalItems > 0">
        <div class="pagination-left">
            <span class="pagination-info">Showing</span>
            <select 
                v-model="localPageSize" 
                @change="updatePageSize" 
                class="page-size-select"
                aria-label="Items per page"
            >
                <option v-for="(size, index) in pageSizeOptions" :key="size" :value="size">
                    {{ size === totalItems ? 'All' : size }}
                </option>
            </select>
            <span class="pagination-info">out of {{ totalItems }}</span>
        </div>
        
        <div class="pagination-controls" @keydown="handleKeydown">
            <button 
                @click="goToPreviousPage" 
                :disabled="currentPage === 1"
                class="pagination-arrow"
                aria-label="Go to previous page"
            >
                <i class="fa-solid fa-chevron-left" aria-hidden="true"></i>
            </button>
            
            <button 
                v-for="(page, index) in pageNumbers" 
                :key="page === '...' ? `dots-${index}` : page"
                @click="goToPage(page)"
                :class="['pagination-number', { 'active': page === currentPage, 'dots': page === '...' }]"
                :disabled="page === '...'"
                :tabindex="page === '...' ? '-1' : '0'"
                :aria-label="page === '...' ? null : (page === currentPage ? `Current page ${page}` : `Go to page ${page}`)"
                :aria-current="page === currentPage ? 'page' : null"
                :aria-hidden="page === '...' ? 'true' : null"
            >
                {{ page }}
            </button>
            
            <button 
                @click="goToNextPage" 
                :disabled="currentPage === totalPages"
                class="pagination-arrow"
                aria-label="Go to next page"
            >
                <i class="fa-solid fa-chevron-right" aria-hidden="true"></i>
            </button>
        </div>
    </div>
</template>

<script>
import adaMixin from '../js/mixins/adaMixin.js';

export default {
    name: 'pagination',
    mixins: [adaMixin],
    BASE_OPTIONS: [100, 250, 500],
    props: {
        totalItems: {
            type: Number,
            required: true
        },
        pageSize: {
            type: Number,
            default: 10
        },
        currentPage: {
            type: Number,
            default: 1
        }
    },
    data() {
        return {
            localPageSize: this.pageSize
        };
    },
    computed: {
        pageSizeOptions() {
            const options = [];
            
            for (const option of this.$options.BASE_OPTIONS) {
                if (option < this.totalItems) {
                    options.push(option);
                }
            }
          
            options.push(this.totalItems);
            
            
            return options;
        },
        totalPages() {
            return Math.ceil(this.totalItems / this.localPageSize);
        },
        pageNumbers() {
            const pages = [];
            const maxPages = 5;
            const halfPages = Math.floor(maxPages / 2);
            
            let startPage = Math.max(1, this.currentPage - halfPages);
            let endPage = Math.min(this.totalPages, startPage + maxPages - 1);
            
            if (endPage - startPage < maxPages - 1) {
                startPage = Math.max(1, endPage - maxPages + 1);
            }
            
            if (startPage > 1) {
                pages.push(1);
                if (startPage > 2) {
                    pages.push('...');
                }
            }
            
            for (let i = startPage; i <= endPage; i++) {
                pages.push(i);
            }
            
            if (endPage < this.totalPages) {
                if (endPage < this.totalPages - 1) {
                    pages.push('...');
                }
                pages.push(this.totalPages);
            }
            
            return pages;
        }
    },
    watch: {
        pageSize(newVal) {
            this.localPageSize = newVal;
        },
        totalItems: {
            handler() {
                this.$nextTick(() => {
                    this.adjustPageSizeIfNeeded();
                });
            },
            immediate: false
        }
    },
    mounted() {
        this.adjustPageSizeIfNeeded();
    },
    methods: {
        adjustPageSizeIfNeeded() {
            if (this.totalItems > 0) {
                const availableOptions = [];
                for (const option of this.$options.BASE_OPTIONS) {
                    if (option < this.totalItems) {
                        availableOptions.push(option);
                    }
                }
                availableOptions.push(this.totalItems);
                
                if (!availableOptions.includes(this.localPageSize)) {
                    this.localPageSize = availableOptions.find(option => option >= this.localPageSize) || this.totalItems;
                    
                    if (this.localPageSize > this.totalItems) {
                        this.localPageSize = availableOptions.length > 1 ? 
                            availableOptions[availableOptions.length - 2] : 
                            this.totalItems;
                    }
                    this.$emit('page-size-change', this.localPageSize);
                    this.$emit('page-change', 1);
                }
            }
        },
        goToPage(page) {
            if (page !== '...' && page >= 1 && page <= this.totalPages) {
                this.$emit('page-change', page);
                this.setAndClearAnnouncement(`Page ${page} loaded.`, 'Navigation');
            }
        },
        goToPreviousPage() {
            if (this.currentPage > 1) {
                const newPage = this.currentPage - 1;
                this.$emit('page-change', newPage);
                this.setAndClearAnnouncement(`Page ${newPage} loaded.`);
            }
        },
        goToNextPage() {
            if (this.currentPage < this.totalPages) {
                const newPage = this.currentPage + 1;
                this.$emit('page-change', newPage);
                this.setAndClearAnnouncement(`Page ${newPage} loaded.`);
            }
        },
        updatePageSize() {
            this.$emit('page-size-change', this.localPageSize);
            this.$emit('page-change', 1);
            const sizeText = this.localPageSize === this.totalItems ? 'All' : this.localPageSize;
            this.setAndClearAnnouncement(`Page size changed to ${sizeText} items per page.`);
        },
        handleKeydown(event) {
            if (event.key === 'ArrowLeft') {
                event.preventDefault();
                if (this.currentPage > 1) {
                    this.goToPreviousPage();
                }
            } else if (event.key === 'ArrowRight') {
                event.preventDefault();
                if (this.currentPage < this.totalPages) {
                    this.goToNextPage();
                }
            }
        }
    }
};
</script>

<style scoped>
.pagination-container {
    display: flex;
    justify-content: space-between;
    align-items: center;
    flex-wrap: wrap;
    gap: 16px;
    padding: 0 20px;
    margin: 20px 0;
}

.pagination-left {
    display: flex;
    align-items: center;
    gap: 8px;
}

.pagination-info {
    font-size: 14px;
    color: rgba(138, 140, 144, 1);
}

.page-size-select {
    padding: 4px 8px;
    border: 1px solid rgba(225, 225, 226, 1);
    border-radius: 4px;
    font-size: 14px;
    background-color: white;
}

.pagination-controls {
    display: flex;
    align-items: center;
    gap: 4px;
}

.pagination-arrow,
.pagination-number {
    padding: 5px 8px;
    margin: 0 4px;
    font-size: 12px;
    border-radius: 8px;
    cursor: pointer;
    background-color: rgba(246, 246, 247, 1);
    border: 1px solid rgba(225, 225, 226, 0.5);
    color: #333;
    transition: all 0.2s;
}

.pagination-arrow:hover:not(:disabled),
.pagination-number:hover:not(:disabled):not(.dots) {
    background-color: rgba(225, 225, 226, 1);
}

.pagination-arrow:disabled,
.pagination-number:disabled {
    opacity: 0.3;
    pointer-events: none;
}

.pagination-number.active {
    background-color: #C2E66E;
    border-color: rgba(194, 230, 110, 0.5);
}

.pagination-number.dots {
    background-color: transparent;
    cursor: default;
    padding: 5px 4px;
    border: none;
}

.pagination-arrow i {
    font-size: 12px;
}

@media (max-width: 768px) {
    .pagination-container {
        justify-content: center;
    }
    
    .pagination-left {
        order: 2;
        width: 100%;
        justify-content: center;
    }
    
    .pagination-controls {
        order: 1;
        width: 100%;
        justify-content: center;
    }
}

@media (max-width: 480px) {
    .pagination-arrow,
    .pagination-number {
        padding: 5px 6px;
        margin: 0 2px;
        font-size: 11px;
    }
    
    .pagination-arrow i {
        font-size: 11px;
    }
}
</style>